import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Stage, Layer, Image as KonvaImage, Text, Transformer, Rect } from "react-konva";
import Sidebar from "../../../components/Sidebar";
import { getBrokerToolTemplate, saveBrokerEditingTemplate } from "../../../utils/apiClient";
import AppConstants from "../../../utils/appConstants";
import {
  getPermissionControllers,
  hasBrokerPermission,
  isBroker,
} from "../../../utils/permissionHelper";
import PermissionBrokerModelController from "../../../controller/model/PermissionBrokerModelController";
import { useToast } from "../../../contexts/ToastContext";
import "../../css/IbDashboard.css";
import "../../css/IbBrokerTools.css";
import "../../css/IbBrokerToolsEditor.css";

const DRAFT_PREFIX = "ibBrokerToolEditorDraft_v1::";
const LIBRARY_PREFIX = "ibBrokerToolImageLibrary_v1::";
const MAX_LIBRARY_IMAGES = 16;

/** Preset text colors (hex) for quick selection on templates. */
const TEXT_FILL_PRESETS = [
  "#0f172a",
  "#ffffff",
  "#fbbf24",
  "#f59e0b",
  "#dc2626",
  "#2563eb",
  "#059669",
  "#a855f7",
  "#64748b",
  "#000000",
];

const normalizeHex6 = (v) => {
  if (typeof v !== "string") return "#0f172a";
  let s = v.trim();
  if (!s) return "#0f172a";
  if (!s.startsWith("#")) s = `#${s}`;
  if (/^#[0-9A-Fa-f]{6}$/i.test(s)) return s.slice(0, 7).toLowerCase();
  if (/^#[0-9A-Fa-f]{3}$/i.test(s)) {
    const r = s[1];
    const g = s[2];
    const b = s[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return "#0f172a";
};

const newId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const userStorageKey = (user) => {
  if (!user || typeof user !== "object") return "guest";
  const k =
    user.USER_CODE ??
    user.user_code ??
    user.USER_ID ??
    user.user_id ??
    user.ID ??
    user.id ??
    user.EMAIL ??
    user.email;
  return String(k || "guest");
};

/** Name/phone for server-side broker edit log; derived from session profile (no UI). */
function pickSilentEditContact(user) {
  if (!user || typeof user !== "object") {
    return { name: "IB User", phone: "n/a" };
  }
  const uid = user.USER_ID ?? user.user_id ?? user.ID ?? user.id ?? "";
  const nameRaw =
    user.NICK_NAME ??
    user.nick_name ??
    user.FULL_NAME ??
    user.full_name ??
    user.USER_NAME ??
    user.user_name ??
    user.EMAIL ??
    user.email ??
    (uid !== "" && uid != null ? `User_${uid}` : "IB User");
  const name = String(nameRaw).trim().slice(0, 250) || "IB User";
  const phoneRaw =
    user.PHONE ?? user.phone ?? user.MOBILE ?? user.mobile ?? user.TELEPHONE ?? user.telephone ?? "";
  const phone = String(phoneRaw).trim().slice(0, 62) || "n/a";
  return { name, phone };
}

const inferMediaFromBlob = (blob) => {
  const ct = String(blob?.type || "").toLowerCase();
  if (ct.startsWith("video/")) return "video";
  if (ct.startsWith("image/")) return "image";
  return "unknown";
};

async function fileToJpegDataUrl(file, maxSide = 1600, quality = 0.88) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const u = URL.createObjectURL(file);
    img.onload = () => {
      try {
        URL.revokeObjectURL(u);
        let w = img.naturalWidth;
        let h = img.naturalHeight;
        if (w < 1 || h < 1) {
          resolve(null);
          return;
        }
        if (w > maxSide || h > maxSide) {
          const r = Math.min(maxSide / w, maxSide / h);
          w = Math.round(w * r);
          h = Math.round(h * r);
        }
        const c = document.createElement("canvas");
        c.width = w;
        c.height = h;
        const ctx = c.getContext("2d");
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0, w, h);
        resolve(c.toDataURL("image/jpeg", quality));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => {
      try {
        URL.revokeObjectURL(u);
      } catch (_) {}
      reject(new Error("Image load failed"));
    };
    img.src = u;
  });
}

function loadHtmlImage(src) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = src;
  });
}

function BrokerToolKonvaImage({
  item,
  onSelect,
  onDragEnd,
  onTransformEnd,
}) {
  const [kimg, setKimg] = useState(null);
  useEffect(() => {
    let alive = true;
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (alive) setKimg(img);
    };
    img.onerror = () => {
      if (alive) setKimg(null);
    };
    img.src = item.imageSrc;
    return () => {
      alive = false;
    };
  }, [item.imageSrc]);

  if (!kimg) return null;

  return (
    <KonvaImage
      id={item.id}
      image={kimg}
      x={item.x}
      y={item.y}
      width={item.width}
      height={item.height}
      rotation={item.rotation || 0}
      draggable
      onMouseDown={(e) => {
        e.cancelBubble = true;
      }}
      onTouchStart={(e) => {
        e.cancelBubble = true;
      }}
      onClick={() => onSelect(item.id)}
      onTap={() => onSelect(item.id)}
      onDragEnd={onDragEnd}
      onTransformEnd={onTransformEnd}
    />
  );
}

export default function BrokerToolEditor() {
  const { t } = useTranslation("mobile");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { error: showError, success: showSuccess } = useToast();

  const templateName = useMemo(() => {
    const raw = searchParams.get("template") || "";
    return String(raw).trim();
  }, [searchParams]);
  const toolTitle = useMemo(() => {
    const raw = searchParams.get("title") || "";
    return String(raw).trim();
  }, [searchParams]);

  const brokerToolId = useMemo(() => {
    const raw = searchParams.get("toolId") || searchParams.get("brokerToolId") || "";
    const n = parseInt(String(raw).trim(), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [searchParams]);

  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(true);
  const [bgImage, setBgImage] = useState(null);
  const [stageSize, setStageSize] = useState({ width: 1080, height: 1080 });
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [imageLibrary, setImageLibrary] = useState([]);
  const [exporting, setExporting] = useState(false);

  const stageRef = useRef(null);
  const transformerRef = useRef(null);
  const fileInputRef = useRef(null);
  const bgUrlRef = useRef(null);

  const permissions = useMemo(() => getPermissionControllers(user), [user]);
  const { brokerPerm, brokerDevicePerm } = permissions;

  const canViewBrokerTools = useMemo(() => {
    if (!isBroker(user)) return false;
    return hasBrokerPermission(
      brokerPerm,
      brokerDevicePerm,
      PermissionBrokerModelController.PER__CAN_VIEW_BROKER_TOOLS
    );
  }, [user, brokerPerm, brokerDevicePerm]);

  const draftKey = useMemo(() => {
    if (!templateName) return null;
    return `${DRAFT_PREFIX}${userStorageKey(user)}::${templateName}`;
  }, [user, templateName]);

  const libraryKey = useMemo(() => `${LIBRARY_PREFIX}${userStorageKey(user)}`, [user]);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem("userData");
      if (stored) setUser(JSON.parse(stored));
    } catch (_) {
      setUser(null);
    } finally {
      setLoadingUser(false);
    }
  }, []);

  useEffect(() => {
    if (!libraryKey) return;
    try {
      const raw = localStorage.getItem(libraryKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) setImageLibrary(parsed.slice(0, MAX_LIBRARY_IMAGES));
    } catch (_) {}
  }, [libraryKey]);

  const addToLibrary = useCallback(
    (dataUrl) => {
      if (!dataUrl || typeof dataUrl !== "string") return;
      const entry = { id: newId(), dataUrl, createdAt: Date.now() };
      setImageLibrary((prev) => {
        const withoutDup = prev.filter((p) => p.dataUrl !== dataUrl);
        const next = [entry, ...withoutDup].slice(0, MAX_LIBRARY_IMAGES);
        try {
          localStorage.setItem(libraryKey, JSON.stringify(next));
        } catch (_) {
          showError(
            t("ib_broker_tools_editor", { defaultValue: "Template editor" }),
            t("ib_broker_tools_library_full", { defaultValue: "Could not save image library (storage full)." })
          );
        }
        return next;
      });
    },
    [libraryKey, showError, t]
  );

  const loadOriginalTemplate = useCallback(async () => {
    if (!templateName) return false;
    setLoadingTemplate(true);
    setLoadError(null);
    try {
      const res = await getBrokerToolTemplate(templateName, { original: true, cacheBust: true });
      const blob = res?.data;
      if (!blob || !(blob instanceof Blob)) {
        throw new Error("Invalid template response");
      }
      if (inferMediaFromBlob(blob) !== "image") {
        throw new Error(
          t("ib_broker_tools_editor_image_only", {
            defaultValue: "This template must be an image to open in the editor.",
          })
        );
      }
      if (bgUrlRef.current) {
        try {
          URL.revokeObjectURL(bgUrlRef.current);
        } catch (_) {}
        bgUrlRef.current = null;
      }
      const url = URL.createObjectURL(blob);
      bgUrlRef.current = url;
      const img = await loadHtmlImage(url);
      const w = img.naturalWidth || img.width || 1080;
      const h = img.naturalHeight || img.height || 1080;
      setStageSize({ width: w, height: h });
      setBgImage(img);
      if (draftKey) {
        try {
          localStorage.removeItem(draftKey);
        } catch (_) {}
      }
      setItems([]);
      setSelectedId(null);
      return true;
    } catch (err) {
      setLoadError(err?.message || "Failed to load template.");
      setBgImage(null);
      return false;
    } finally {
      setLoadingTemplate(false);
    }
  }, [templateName, draftKey, t]);

  useEffect(() => {
    if (loadingUser) return;
    if (!canViewBrokerTools || !templateName) return;

    let cancelled = false;
    (async () => {
      await loadOriginalTemplate();
      if (cancelled) return;
    })();

    return () => {
      cancelled = true;
    };
  }, [loadingUser, canViewBrokerTools, templateName, loadOriginalTemplate]);

  useEffect(() => {
    return () => {
      if (bgUrlRef.current) {
        try {
          URL.revokeObjectURL(bgUrlRef.current);
        } catch (_) {}
        bgUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const tr = transformerRef.current;
    const stage = stageRef.current;
    if (!tr || !stage) return;
    if (!selectedId) {
      tr.nodes([]);
      tr.getLayer()?.batchDraw();
      return;
    }
    const node = stage.findOne((n) => n.id() === selectedId);
    if (node) {
      tr.nodes([node]);
    } else {
      tr.nodes([]);
    }
    tr.getLayer()?.batchDraw();
  }, [selectedId, items, bgImage, loadingTemplate]);

  const displayScale = useMemo(() => {
    const maxW = typeof window !== "undefined" ? Math.min(920, Math.max(280, window.innerWidth - 340)) : 600;
    const s = Math.min(maxW / stageSize.width, 1);
    return Number.isFinite(s) && s > 0 ? s : 1;
  }, [stageSize.width]);

  const updateItem = useCallback((id, patch) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }, []);

  const handleDragEnd = useCallback(
    (id) => (e) => {
      const n = e.target;
      updateItem(id, { x: n.x(), y: n.y() });
    },
    [updateItem]
  );

  const handleImageTransformEnd = useCallback(
    (id) => (e) => {
      const node = e.target;
      const scaleX = node.scaleX();
      const scaleY = node.scaleY();
      node.scaleX(1);
      node.scaleY(1);
      const width = Math.max(32, node.width() * scaleX);
      const height = Math.max(32, node.height() * scaleY);
      updateItem(id, { x: node.x(), y: node.y(), width, height, rotation: node.rotation() });
    },
    [updateItem]
  );

  const handleTextTransformEnd = useCallback(
    (id) => (e) => {
      const node = e.target;
      const scaleX = node.scaleX();
      const scaleY = node.scaleY();
      node.scaleX(1);
      node.scaleY(1);
      const width = Math.max(40, node.width() * scaleX);
      const fs = Math.max(10, (node.fontSize() || 24) * scaleY);
      updateItem(id, {
        x: node.x(),
        y: node.y(),
        width,
        fontSize: fs,
        rotation: node.rotation(),
      });
    },
    [updateItem]
  );

  const addTextLayer = useCallback(
    (kind) => {
      const isDesc = kind === "description";
      const id = newId();
      const fontSize = isDesc ? 22 : 34;
      const text = isDesc
        ? t("ib_broker_tools_editor_desc_placeholder", { defaultValue: "Description text" })
        : t("ib_broker_tools_editor_title_placeholder", { defaultValue: "Title text" });
      setItems((prev) => [
        ...prev,
        {
          id,
          type: isDesc ? "description" : "text",
          x: stageSize.width * 0.08,
          y: stageSize.height * (isDesc ? 0.55 : 0.12),
          width: stageSize.width * 0.84,
          height: fontSize * 4,
          rotation: 0,
          text,
          fontSize,
          fill: "#0f172a",
        },
      ]);
      setSelectedId(id);
    },
    [stageSize.width, stageSize.height, t]
  );

  const addImageFromDataUrl = useCallback(
    (dataUrl) => {
      if (!dataUrl) return;
      const img = new window.Image();
      img.onload = () => {
        const maxW = stageSize.width * 0.45;
        const maxH = stageSize.height * 0.45;
        let w = img.naturalWidth || img.width;
        let h = img.naturalHeight || img.height;
        if (w < 1) w = 200;
        if (h < 1) h = 200;
        const r = Math.min(maxW / w, maxH / h, 1);
        w = Math.round(w * r);
        h = Math.round(h * r);
        const id = newId();
        setItems((prev) => [
          ...prev,
          {
            id,
            type: "image",
            x: stageSize.width * 0.25,
            y: stageSize.height * 0.25,
            width: w,
            height: h,
            rotation: 0,
            imageSrc: dataUrl,
          },
        ]);
        setSelectedId(id);
      };
      img.src = dataUrl;
    },
    [stageSize.width, stageSize.height]
  );

  const onPickFile = useCallback(
    async (e) => {
      const file = e.target?.files?.[0];
      e.target.value = "";
      if (!file || !file.type.startsWith("image/")) {
        showError(
          t("ib_broker_tools_editor", { defaultValue: "Template editor" }),
          t("ib_broker_tools_editor_need_image", { defaultValue: "Please choose an image file." })
        );
        return;
      }
      try {
        const dataUrl = await fileToJpegDataUrl(file, 2000, 0.9);
        if (!dataUrl) throw new Error("Could not read image");
        addToLibrary(dataUrl);
        addImageFromDataUrl(dataUrl);
      } catch (err) {
        showError(
          t("ib_broker_tools_editor", { defaultValue: "Template editor" }),
          err?.message || "Failed to add image."
        );
      }
    },
    [addImageFromDataUrl, addToLibrary, showError, t]
  );

  const deleteSelected = useCallback(() => {
    if (!selectedId) return;
    setItems((prev) => prev.filter((it) => it.id !== selectedId));
    setSelectedId(null);
  }, [selectedId]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Delete" || e.key === "Backspace") {
        const tag = (e.target && e.target.tagName) || "";
        if (tag === "INPUT" || tag === "TEXTAREA") return;
        deleteSelected();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [deleteSelected]);

  const selectedItem = useMemo(() => items.find((i) => i.id === selectedId) || null, [items, selectedId]);

  const handleDownloadPng = useCallback(async () => {
    const stage = stageRef.current;
    const tr = transformerRef.current;
    if (!stage) return;

    const { name, phone } = pickSilentEditContact(user);

    try {
      setExporting(true);
      if (tr) {
        tr.nodes([]);
        tr.getLayer()?.batchDraw();
      }
      stage.batchDraw();
      const dataUrl = stage.toDataURL({
        pixelRatio: 1,
        mimeType: "image/png",
        quality: 1,
      });

      if (brokerToolId != null) {
        try {
          const blob = await (await fetch(dataUrl)).blob();
          const res = await saveBrokerEditingTemplate({
            brokerToolId,
            name,
            phone,
            imageBlob: blob,
          });
          if (Number(res?.data?.CODE) !== AppConstants.responseOk) {
            /* keep silent: download still proceeds */
          }
        } catch (_) {
          /* silent: offline or save failure should not block download */
        }
      }

      const a = document.createElement("a");
      const safe = templateName.replace(/[^\w-]+/g, "_").slice(0, 80) || "template";
      a.download = `broker-tool-${safe}.png`;
      a.href = dataUrl;
      a.click();
      showSuccess(
        t("ib_broker_tools_editor_downloaded", { defaultValue: "Image downloaded." }),
        ""
      );
    } catch (err) {
      showError(
        t("ib_broker_tools_editor", { defaultValue: "Template editor" }),
        err?.message || "Export failed."
      );
    } finally {
      setExporting(false);
      if (selectedId && transformerRef.current && stageRef.current) {
        const found = stageRef.current.findOne((n) => n.id() === selectedId);
        if (found) transformerRef.current.nodes([found]);
        transformerRef.current.getLayer()?.batchDraw();
      }
    }
  }, [brokerToolId, user, selectedId, showError, showSuccess, t, templateName]);

  const clearDraft = useCallback(async () => {
    await loadOriginalTemplate();
    showSuccess(
      t("ib_broker_tools_editor_draft_cleared", { defaultValue: "Template reset to original." }),
      ""
    );
  }, [loadOriginalTemplate, showSuccess, t]);

  const pageHeading = toolTitle || templateName || t("ib_broker_tools_editor", { defaultValue: "Template editor" });

  if (!loadingUser && !canViewBrokerTools) {
    return (
      <div className="ib-dashboard-page ib-broker-tools-page">
        <Sidebar user={user} />
        <div className="ib-dashboard-main">
          <div className="container-fluid px-0 w-100 ib-dashboard-main-inner">
            <div className="ib-broker-tools-denied" role="alert">
              {t("ib_broker_tools_no_permission", {
                defaultValue: "You do not have permission to view broker tools.",
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!templateName) {
    return (
      <div className="ib-dashboard-page ib-broker-tools-page">
        <Sidebar user={user} />
        <div className="ib-dashboard-main">
          <div className="container-fluid px-0 w-100 ib-dashboard-main-inner ib-broker-tools-editor-wrap">
            <p className="ib-broker-tools-editor-error">
              {t("ib_broker_tools_editor_missing_template", { defaultValue: "No template selected." })}
            </p>
            <button type="button" className="btn btn-light ib-broker-tools-editor-back" onClick={() => navigate("/pages/ibDashboard/broker-tools")}>
              {t("ib_broker_tools_editor_back", { defaultValue: "Back to broker tools" })}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ib-dashboard-page ib-broker-tools-page ib-broker-tools-editor-page">
      <Sidebar user={user} />
      <div className="ib-dashboard-main">
        <div className="container-fluid px-0 w-100 ib-dashboard-main-inner ib-broker-tools-editor-wrap">
          <header className="ib-broker-tools-editor-head">
            <h1 className="ib-broker-tools-editor-title">{pageHeading}</h1>
            <p className="ib-broker-tools-editor-sub">
              {t("ib_broker_tools_editor_sub", {
                defaultValue: "Drag text and images on the template. Each visit starts from the original template.",
              })}
            </p>
          </header>

          {loadError && (
            <div className="ib-broker-tools-editor-error" role="alert">
              {loadError}
              <div className="mt-2">
                <button type="button" className="btn btn-light btn-sm" onClick={() => navigate("/pages/ibDashboard/broker-tools")}>
                  {t("ib_broker_tools_editor_back", { defaultValue: "Back" })}
                </button>
              </div>
            </div>
          )}

          {!loadError && (
            <div className="ib-broker-tools-editor-layout">
              <aside className="ib-broker-tools-editor-sidebar">
                <div className="ib-broker-tools-editor-panel ib-broker-tools-editor-tools">
                  <span className="ib-broker-tools-editor-tools-label">
                    {t("ib_broker_tools_editor_add", { defaultValue: "Add" })}
                  </span>
                  <div className="ib-broker-tools-editor-btn-stack">
                    <button
                      type="button"
                      className="ib-broker-tools-editor-btn ib-broker-tools-editor-btn--gold"
                      onClick={() => addTextLayer("text")}
                    >
                      <i className="fa-solid fa-font" aria-hidden />
                      {t("ib_broker_tools_editor_add_title", { defaultValue: "Title" })}
                    </button>
                    <button type="button" className="ib-broker-tools-editor-btn ib-broker-tools-editor-btn--ghost" onClick={() => addTextLayer("description")}>
                      <i className="fa-solid fa-align-left" aria-hidden />
                      {t("ib_broker_tools_editor_add_desc", { defaultValue: "Description" })}
                    </button>
                    <button type="button" className="ib-broker-tools-editor-btn ib-broker-tools-editor-btn--ghost" onClick={() => fileInputRef.current?.click()}>
                      <i className="fa-regular fa-image" aria-hidden />
                      {t("ib_broker_tools_editor_add_image", { defaultValue: "Image" })}
                    </button>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    tabIndex={-1}
                    aria-hidden="true"
                    className="ib-broker-tools-editor-file-input-hidden"
                    onChange={onPickFile}
                  />
                </div>

                {imageLibrary.length > 0 && (
                  <div className="ib-broker-tools-editor-panel ib-broker-tools-editor-library">
                    <div className="ib-broker-tools-editor-library-head">
                      {t("ib_broker_tools_editor_library", { defaultValue: "My images" })}
                    </div>
                    <div className="ib-broker-tools-editor-library-grid">
                      {imageLibrary.map((entry) => (
                        <button
                          key={entry.id}
                          type="button"
                          className="ib-broker-tools-editor-library-thumb"
                          title={t("ib_broker_tools_editor_add_to_canvas", { defaultValue: "Add to canvas" })}
                          onClick={() => addImageFromDataUrl(entry.dataUrl)}
                        >
                          <img src={entry.dataUrl} alt="" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="ib-broker-tools-editor-panel ib-broker-tools-editor-props">
                  <span className="ib-broker-tools-editor-tools-label">
                    {t("ib_broker_tools_editor_selection", { defaultValue: "Selection" })}
                  </span>
                  {selectedItem && (selectedItem.type === "text" || selectedItem.type === "description") ? (
                    <>
                      <label className="ib-broker-tools-editor-label" htmlFor="bt-edit-text">
                        {t("ib_broker_tools_editor_text", { defaultValue: "Text" })}
                      </label>
                      <textarea
                        id="bt-edit-text"
                        className="ib-broker-tools-editor-textarea"
                        rows={4}
                        value={selectedItem.text || ""}
                        onChange={(e) => updateItem(selectedItem.id, { text: e.target.value })}
                      />
                      <label className="ib-broker-tools-editor-label ib-broker-tools-editor-label--spaced" htmlFor="bt-edit-fs">
                        {t("ib_broker_tools_editor_font_size", { defaultValue: "Size" })}
                      </label>
                      <input
                        id="bt-edit-fs"
                        type="number"
                        className="ib-broker-tools-editor-input"
                        min={10}
                        max={200}
                        value={Math.round(selectedItem.fontSize || 24)}
                        onChange={(e) =>
                          updateItem(selectedItem.id, { fontSize: Math.max(10, Number(e.target.value) || 10) })
                        }
                      />
                      <label className="ib-broker-tools-editor-label ib-broker-tools-editor-label--spaced" htmlFor="bt-edit-color">
                        {t("ib_broker_tools_editor_text_color", { defaultValue: "Text color" })}
                      </label>
                      <div className="ib-broker-tools-editor-color-block">
                        <input
                          id="bt-edit-color"
                          type="color"
                          className="ib-broker-tools-editor-color-native"
                          value={normalizeHex6(selectedItem.fill)}
                          onChange={(e) => updateItem(selectedItem.id, { fill: normalizeHex6(e.target.value) })}
                          aria-label={t("ib_broker_tools_editor_text_color", { defaultValue: "Text color" })}
                        />
                        <div className="ib-broker-tools-editor-color-swatches" role="list">
                          {TEXT_FILL_PRESETS.map((hex) => {
                            const active = normalizeHex6(selectedItem.fill) === hex;
                            return (
                              <button
                                key={hex}
                                type="button"
                                role="listitem"
                                className={`ib-broker-tools-editor-swatch${active ? " ib-broker-tools-editor-swatch--active" : ""}`}
                                style={{ backgroundColor: hex }}
                                title={hex}
                                aria-label={hex}
                                aria-pressed={active}
                                onClick={() => updateItem(selectedItem.id, { fill: hex })}
                              />
                            );
                          })}
                        </div>
                      </div>
                    </>
                  ) : selectedItem && selectedItem.type === "image" ? (
                    <p className="ib-broker-tools-editor-hint">
                      {t("ib_broker_tools_editor_image_hint", {
                        defaultValue: "Drag corners to resize. Delete key removes the layer.",
                      })}
                    </p>
                  ) : (
                    <p className="ib-broker-tools-editor-hint">
                      {t("ib_broker_tools_editor_select_hint", {
                        defaultValue: "Click a layer to edit or resize.",
                      })}
                    </p>
                  )}
                  <div className="ib-broker-tools-editor-actions">
                    <button
                      type="button"
                      className="ib-broker-tools-editor-btn ib-broker-tools-editor-btn--danger ib-broker-tools-editor-btn--block"
                      disabled={!selectedId}
                      onClick={deleteSelected}
                    >
                      {t("ib_broker_tools_editor_delete_layer", { defaultValue: "Remove layer" })}
                    </button>
                  </div>
                </div>

                <div className="ib-broker-tools-editor-panel ib-broker-tools-editor-footer-actions">
                  <button type="button" className="ib-broker-tools-editor-btn ib-broker-tools-editor-btn--muted ib-broker-tools-editor-btn--block" onClick={clearDraft}>
                    {t("ib_broker_tools_editor_reset", { defaultValue: "Reset canvas" })}
                  </button>
                  <button
                    type="button"
                    className="ib-broker-tools-editor-btn ib-broker-tools-editor-btn--download ib-broker-tools-editor-btn--block"
                    disabled={loadingTemplate || !bgImage || exporting}
                    onClick={handleDownloadPng}
                  >
                    {exporting ? (
                      t("ib_broker_tools_editor_exporting", { defaultValue: "Working…" })
                    ) : (
                      <>
                        <i className="fa-solid fa-download" aria-hidden />
                        {t("ib_broker_tools_editor_download", { defaultValue: "Download PNG" })}
                      </>
                    )}
                  </button>
                </div>
              </aside>

              <div className="ib-broker-tools-editor-canvas-wrap">
                {loadingTemplate && (
                  <div className="ib-broker-tools-editor-loading" aria-busy="true">
                    <span className="ib-dashboard-skeleton ib-dashboard-skeleton--kpi-value" style={{ width: "120px", height: "20px" }} />
                  </div>
                )}
                {!loadingTemplate && bgImage && (
                  <div
                    className="ib-broker-tools-editor-stage-outer"
                    style={{
                      width: stageSize.width * displayScale,
                      height: stageSize.height * displayScale,
                    }}
                  >
                    <div
                      className="ib-broker-tools-editor-stage-scaler"
                      style={{
                        width: stageSize.width,
                        height: stageSize.height,
                        transform: `scale(${displayScale})`,
                        transformOrigin: "top left",
                      }}
                    >
                      <Stage ref={stageRef} width={stageSize.width} height={stageSize.height}>
                        <Layer listening={false}>
                          <KonvaImage
                            image={bgImage}
                            x={0}
                            y={0}
                            width={stageSize.width}
                            height={stageSize.height}
                            listening={false}
                          />
                        </Layer>
                        <Layer>
                          <Rect
                            x={0}
                            y={0}
                            width={stageSize.width}
                            height={stageSize.height}
                            fill="transparent"
                            onMouseDown={() => setSelectedId(null)}
                            onTouchStart={() => setSelectedId(null)}
                          />
                          {items.map((it) => {
                            if (it.type === "image") {
                              return (
                                <BrokerToolKonvaImage
                                  key={it.id}
                                  item={it}
                                  onSelect={setSelectedId}
                                  onDragEnd={handleDragEnd(it.id)}
                                  onTransformEnd={handleImageTransformEnd(it.id)}
                                />
                              );
                            }
                            if (it.type === "text" || it.type === "description") {
                              return (
                                <Text
                                  key={it.id}
                                  id={it.id}
                                  text={it.text || ""}
                                  x={it.x}
                                  y={it.y}
                                  width={it.width}
                                  fontSize={it.fontSize}
                                  fill={normalizeHex6(it.fill)}
                                  rotation={it.rotation || 0}
                                  draggable
                                  wrap="word"
                                  onMouseDown={(e) => {
                                    e.cancelBubble = true;
                                  }}
                                  onTouchStart={(e) => {
                                    e.cancelBubble = true;
                                  }}
                                  onClick={() => setSelectedId(it.id)}
                                  onTap={() => setSelectedId(it.id)}
                                  onDragEnd={handleDragEnd(it.id)}
                                  onTransformEnd={handleTextTransformEnd(it.id)}
                                />
                              );
                            }
                            return null;
                          })}
                          <Transformer
                            ref={transformerRef}
                            rotateEnabled
                            borderStroke="#fbbf24"
                            anchorStroke="#fbbf24"
                            anchorFill="#0f172a"
                            boundBoxFunc={(oldBox, newBox) => {
                              if (newBox.width < 24 || newBox.height < 24) return oldBox;
                              return newBox;
                            }}
                          />
                        </Layer>
                      </Stage>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
